import { useLiveQuery } from 'dexie-react-hooks';
import { Camera, ImageOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ReproStatus, Stage } from '@dairy/domain';
import { newId } from '@dairy/shared';
import { Badge } from '@/components/ui';
import { db } from '@/db/db';
import { cachePhoto } from '@/db/repo';
import { apiFetch } from '@/lib/api';

const STAGE_TONE: Record<string, 'green' | 'blue' | 'purple' | 'amber' | 'neutral' | 'red'> = {
  MILKING: 'green',
  DRY: 'blue',
  HEIFER: 'purple',
  CALF: 'amber',
  BULL: 'neutral',
  SOLD: 'red',
  DEAD: 'red',
  CULLED: 'red',
};

export function StageBadge({ stage }: { stage: Stage }) {
  const { t } = useTranslation();
  return <Badge tone={STAGE_TONE[stage] ?? 'neutral'}>{t(`animals.stage.${stage}`)}</Badge>;
}

export function ReproBadge({ status }: { status: ReproStatus | null | undefined }) {
  const { t } = useTranslation();
  if (!status) return null;
  return <Badge tone={status === 'PREGNANT' ? 'green' : status === 'SERVED' ? 'blue' : 'neutral'}>{t(`animals.repro.${status}`)}</Badge>;
}

/** Device first, then the server (with the auth header); a downloaded photo is kept as "cached". */
export function Photo({ fileId, className, alt = '' }: { fileId: string | null | undefined; className?: string; alt?: string }) {
  const local = useLiveQuery(() => (fileId ? db.fileBlobs.get(fileId) : undefined), [fileId]);
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let revoke: string | null = null;
    if (!fileId) return;
    if (local?.blob) {
      revoke = URL.createObjectURL(local.blob);
      setUrl(revoke);
    } else if (local === undefined && navigator.onLine) {
      apiFetch(`/files/${fileId}`)
        .then((r) => r.blob())
        .then(async (b) => {
          await cachePhoto(fileId, b);
        })
        .catch(() => setFailed(true));
    }
    return () => {
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [fileId, local]);
  if (!fileId) return null;
  if (!url) return <div className={`flex items-center justify-center bg-stone-200 text-stone-500 ${className ?? ''}`}>{failed ? <ImageOff className="h-6 w-6" /> : null}</div>;
  return <img src={url} alt={alt} className={`object-cover ${className ?? ''}`} />;
}

/** S4-T11: shrink a photo to longest side 1280 px, JPEG quality 0.7, before saving it. */
export async function shrinkPhoto(file: File): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, 1280 / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('encode failed'))), 'image/jpeg', 0.7));
}

export interface PickedPhoto {
  id: string;
  blob: Blob;
  url: string;
}

/** Camera / gallery picker for a photo (animal or receipt). */
export function PhotoPicker({ value, onChange, existingFileId }: { value: PickedPhoto | null; onChange: (p: PickedPhoto | null) => void; existingFileId?: string | null }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-3">
      {value ? (
        <img src={value.url} alt="" className="h-20 w-20 rounded-xl object-cover" />
      ) : existingFileId ? (
        <Photo fileId={existingFileId} className="h-20 w-20 rounded-xl" />
      ) : null}
      <label className="inline-flex min-h-12 cursor-pointer items-center gap-2 rounded-xl border border-stone-300 bg-white px-4 font-medium">
        <Camera className="h-5 w-5" />
        {value || existingFileId ? t('photo.replace') : t('photo.take')}
        <input
          type="file"
          accept="image/*"
          capture="environment"
          className="sr-only"
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            const blob = await shrinkPhoto(file);
            onChange({ id: newId(), blob, url: URL.createObjectURL(blob) });
          }}
        />
      </label>
      {value ? (
        <button type="button" className="min-h-11 px-2 text-sm text-stone-600" onClick={() => onChange(null)}>
          {t('common.remove')}
        </button>
      ) : null}
    </div>
  );
}
